import { useCallback, useMemo, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { Close, Create } from "@mui/icons-material";
import { Box, Link, Typography } from "@mui/material";

import { MAX_SECONDARY_RESIDENCE_LIABILITY_ADDRESSES } from "@covertree/consts";
import {
  CoverageSecondaryResidenceLiability,
  SecondaryResidenceLiabilityAddress,
} from "@covertree/new-business-graphql";
import { theme } from "@covertree/theme";
import { Button, FormTextField, FormToggleCard, Modal } from "@covertree/ui";
import { formatCurrency } from "@covertree/utils";

interface SecondaryResidenceLiabilityContentProps {
  secondaryResidenceLiabilityDetails: Omit<
    CoverageSecondaryResidenceLiability,
    "valueIsHidden"
  > & {
    description: string;
    image: string;
    name: string;
    monthlyPrice: number;
    yearlyPrice: number;
    tooltip: string;
    valueIsCustomizable: boolean;
  };
}

export function SecondaryResidenceLiabilityCard({
  secondaryResidenceLiabilityDetails: {
    description,
    image,
    name,
    monthlyPrice,
    yearlyPrice,
    tooltip,
    valueIsCustomizable,
  },
}: SecondaryResidenceLiabilityContentProps) {
  const {
    control,
    clearErrors,
    formState: { isValid },
    trigger,
    getValues,
    setValue,
  } = useFormContext<{
    secondaryResidenceLiability: SecondaryResidenceLiabilityAddress[];
  }>();
  const [valuesOnModalOpen, setValuesOnModalOpen] = useState<
    SecondaryResidenceLiabilityAddress[]
  >([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const { append, remove, fields } = useFieldArray({
    control,
    name: "secondaryResidenceLiability",
  });

  const handleAddAddress = useCallback(() => {
    append({ address: "" });
  }, [append]);

  const handleDeleteItem = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove]
  );

  const handleOpenModal = useCallback(async () => {
    const currentValues = getValues("secondaryResidenceLiability");
    setValuesOnModalOpen(structuredClone(currentValues));

    if (currentValues.length === 0) {
      handleAddAddress();
      clearErrors("secondaryResidenceLiability");
    }

    setIsModalOpen(true);
  }, [isModalOpen, trigger, setIsModalOpen]);

  const handleCloseModal = useCallback(() => {
    setValue("secondaryResidenceLiability", valuesOnModalOpen);
    setIsModalOpen(false);
  }, [valuesOnModalOpen, setIsModalOpen]);

  const handleSave = useCallback(() => {
    const addresses = getValues("secondaryResidenceLiability");
    if (addresses[0].address.length === 0) {
      remove(0);
    }
    setIsModalOpen(false);
  }, [remove, getValues, handleCloseModal]);

  const showAddAddressButton = useMemo(
    () => fields.length < MAX_SECONDARY_RESIDENCE_LIABILITY_ADDRESSES,
    [fields]
  );

  return (
    <FormToggleCard
      title={name}
      image={image}
      tooltip={tooltip}
      actionText={
        monthlyPrice > 0 ? `+ ${formatCurrency(monthlyPrice)}/mo` : undefined
      }
      actionSubText={
        yearlyPrice > 0 ? `(${formatCurrency(yearlyPrice, 2)}/year)` : undefined
      }
      subheader={description}
      subheaderVariant="grey"
      onToggle={
        fields.length > 0
          ? () => {
              remove();
            }
          : handleOpenModal
      }
      checked={fields.length > 0}
      checkedCustomStyles={{
        backgroundColor: theme.palette.secondary.contrastText,
      }}
      customizable={valueIsCustomizable}
      content={
        <>
          {fields.length > 0 && (
            <Link
              component="button"
              type="button"
              onClick={handleOpenModal}
              color="secondary"
              variant="body2"
              underline="always">
              <Box
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "center",
                  "& svg": {
                    marginRight: theme.spacing(1),
                  },
                })}>
                <Create fontSize="inherit" />
                Edit
              </Box>
            </Link>
          )}

          <Modal
            maxWidth="sm"
            title="Secondary Residences"
            open={isModalOpen}
            onClose={handleCloseModal}>
            <Box sx={(theme) => ({ minWidth: theme.spacing(60) })}>
              <Typography variant="h5" gutterBottom>
                Address of Secondary Residence(s)
              </Typography>

              {fields.map((item, index, array) => (
                <Box
                  sx={{ display: "flex", flexDirection: "row", flexGrow: 1 }}
                  pb={1}
                  key={`address-item-${item.id}`}>
                  <Box sx={{ flexGrow: 1 }}>
                    <FormTextField
                      control={control}
                      name={`secondaryResidenceLiability.${index}.address`}
                      dataTestId={`secondary-residence-liability-address-${index}`}
                      id={`secondary-residence-liability-address-${index}`}
                      label=""
                      hiddenLabel
                    />
                  </Box>
                  <Box>
                    {array.length > 1 && (
                      <Link
                        sx={(theme) => ({
                          top: theme.spacing(3),
                          left: theme.spacing(1),
                        })}
                        component="button"
                        onClick={() => handleDeleteItem(index)}
                        variant="body2"
                        underline="always"
                        data-testid="remove-address-button">
                        <Close fontSize="inherit" />
                      </Link>
                    )}
                  </Box>
                </Box>
              ))}

              {showAddAddressButton && (
                <Button
                  variant="outlined"
                  size="small"
                  id="add-next-address"
                  data-testid="add-next-address"
                  onClick={handleAddAddress}>
                  Add next address
                </Button>
              )}

              <Box mt={4}>
                <Button
                  id="save-address-form"
                  data-testid="save-address-form"
                  disabled={!isValid}
                  type="button"
                  onClick={handleSave}>
                  Save
                </Button>
              </Box>
            </Box>
          </Modal>
        </>
      }
    />
  );
}
